import { Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { Field } from "react-final-form";
import QRCode from "react-qr-code";
import AuthService from "../../../services/AuthService";
import { mfaCleared, sessionSuccess } from "../../../store/slices/authSlice";
import { formatKey, MFA_STEP_ENROL } from "../../../helpers/SsoHelper";
import { errorMessageId, errorText, TOO_MANY_ATTEMPTS } from "../../../helpers/ErrorMessage";
import { RouteConstants } from "../../../constants";
import PreLoginContainer from "../../common/preLoginContainer";
import Form from "../../common/form";
import * as Validations from "../../common/form/validator";
import { OtpInput } from "../../common/fields";
import Link from "../../common/link";

// The authenticator step after a correct password (or the emergency
// address): enrol a new authenticator with the QR code or the typed key and
// one code, or enter one code. The half-signed-in token goes with each call.
export const MfaSignIn = ({ mfa, sessionSuccess, mfaCleared }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [enrolment, setEnrolment] = useState(null);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [spent, setSpent] = useState(false);
  const enrolling = mfa?.step === MFA_STEP_ENROL;

  const failed = (reason) => {
    const status = reason?.response?.status;
    const code = reason?.response?.data?.response;
    if (code === TOO_MANY_ATTEMPTS) {
      // Over a rate limit, or the person's MFA step is locked after too many wrong
      // codes: the step stays open to try again after the wait.
      setError({ text: errorText(reason.response.data, intl) });
    } else if (status === 401) {
      setSpent(true);
      setError("mfa_session_expired");
    } else if (code === "MFA_ATTEMPTS_EXCEEDED") {
      setSpent(true);
      setError("mfa_attempts_exceeded");
    } else if (code === "MFA_SECRET_UNREADABLE") {
      // Another code cannot help: an admin resets MFA and the person sets it up again.
      setSpent(true);
      setError("mfa_secret_unreadable");
    } else {
      setError(errorMessageId({ response: code }, intl, "invalid_mfa_code"));
    }
  };

  useEffect(() => {
    if (!enrolling) {
      return undefined;
    }
    let cancelled = false;
    AuthService.mfaEnrol()
      .then((ajax) => {
        if (!cancelled) {
          setEnrolment(ajax.data?.response || null);
        }
      })
      .catch((reason) => {
        if (!cancelled) {
          failed(reason);
        }
      });
    return () => {
      cancelled = true;
    };
    // The key is asked for once per step; failed only sets state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolling]);

  if (!mfa) {
    return <Navigate to={RouteConstants.APP_ROUTE_LOGIN} replace />;
  }

  const submit = async (values) => {
    setRequesting(true);
    setError(null);
    try {
      const ajax = enrolling
        ? await AuthService.mfaConfirm({ code: values.code })
        : await AuthService.mfaVerify({ code: values.code });
      sessionSuccess(ajax.data);
    } catch (reason) {
      failed(reason);
    } finally {
      setRequesting(false);
    }
  };

  const back = () => {
    mfaCleared();
    navigate(
      mfa.emergency
        ? RouteConstants.APP_ROUTE_EMERGENCY_SIGN_IN
        : RouteConstants.APP_ROUTE_LOGIN_LOCAL
    );
    return true;
  };

  return (
    <PreLoginContainer>
      <div className="title text-center">
        {intl.formatMessage({ id: enrolling ? "mfa_enrol_title" : "mfa_verify_title" })}
        <div className="tag-line text-muted">
          {intl.formatMessage({ id: enrolling ? "mfa_enrol_text" : "mfa_verify_text" })}
        </div>
      </div>
      {(enrolling && enrolment && !spent && (
        <div className="flex flex-column align-items-center gap-2 mt-3">
          <div className="mfa-qr" style={{ background: "#ffffff", padding: "24px" }}>
            <QRCode
              value={enrolment.otpAuthUri}
              size={176}
              title={intl.formatMessage({ id: "mfa_qr_label" })}
            />
          </div>
          <div className="text-600">{intl.formatMessage({ id: "mfa_key_label" })}</div>
          <code
            data-testid="mfa-key"
            className="text-center"
            style={{ fontSize: "1.1rem", letterSpacing: "1px" }}
          >
            {formatKey(enrolment.key)
              .split(" ")
              .map((group, index) => (
                <Fragment key={index}>
                  {index > 0 ? " " : null}
                  <span className="white-space-nowrap">{group}</span>
                </Fragment>
              ))}
          </code>
        </div>
      )) ||
        null}
      {(!spent && (
        <Form
          fullWidthButtons
          requesting={requesting}
          submitBtn={{
            label: intl.formatMessage({ id: enrolling ? "mfa_confirm" : "sign_in" }),
            icon: "pi-arrow-right",
            onClick: submit,
          }}
        >
          <div className="flex flex-column gap-2 mt-4">
            <Field
              name="code"
              component={OtpInput}
              label={intl.formatMessage({ id: "mfa_code" })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
        </Form>
      )) ||
        null}
      {(error && (
        <div className="error-message mt-2">
          {error.text || intl.formatMessage({ id: error })}
        </div>
      )) ||
        null}
      <div className="text-center mt-1">
        <Link to={RouteConstants.APP_ROUTE_LOGIN} onClick={back}>
          {intl.formatMessage({ id: "back_to_login" })}
        </Link>
      </div>
    </PreLoginContainer>
  );
};

const mapStateToProps = (state) => ({
  mfa: state.auth.mfa,
});

const mapDispatchToProps = (dispatch) => ({
  sessionSuccess: (data) => dispatch(sessionSuccess(data)),
  mfaCleared: () => dispatch(mfaCleared()),
});

export default connect(mapStateToProps, mapDispatchToProps)(MfaSignIn);
